// ---------------------------------------------------------------------------
// Fetch Batcher
//
// Transparently intercepts GET fetch() and XHR calls whose pathname matches
// one of the BATCHABLE_PATTERNS, groups them during a short debounce window,
// and fires a single POST to Drupal's /subrequests endpoint instead of N
// individual HTTP requests. Each original caller receives a synthetic response
// that is indistinguishable from a real one.
//
// Usage (called once at application startup, typically from main.jsx):
//
//   import { installFetchBatcher, BATCHABLE_PATTERNS } from './services/requestBatcher.js';
//
//   installFetchBatcher({
//     debounceMs: 30,
//     patterns: [...BATCHABLE_PATTERNS, '/api/extra-endpoint'],
//   });
//
// The XHR side is documented in detail below because it is significantly more
// complex than the fetch side. XHR is stateful and event-driven; its response
// properties are read-only on the native prototype and require a different
// override technique. See the "XHR synthetic response" section for details.
// ---------------------------------------------------------------------------

import { _nativeFetch, ensureCsrfToken } from './csrf.js';

// ---------------------------------------------------------------------------
// Batchable endpoint patterns
//
// Each entry is either:
//   - a string: matched as a pathname prefix (pathname.startsWith(entry))
//   - a RegExp: matched against the full pathname
//
// Add new patterns in main.jsx by spreading this array:
//   patterns: [...BATCHABLE_PATTERNS, '/api/new-endpoint']
// ---------------------------------------------------------------------------
export const BATCHABLE_PATTERNS = [
  '/decoupled-api/', // all decoupled_toolbox collection / item endpoints
  '/api/user/info',  // current-user info endpoint
  // Merged per-user menus: the shell asks for 5 of them (main, main_admin,
  // settings, favorites, account) on every mount of Navigation.yaml.
  '/api/orbit/navigation',
];

// ---------------------------------------------------------------------------
// Internal state
// ---------------------------------------------------------------------------

/** @type {Array<{requestId: string, url: string, resolve: Function, reject: Function, xhrEntry: object|null}>} */
let _pending = [];
let _timer = null;
let _counter = 0;
let _patterns = BATCHABLE_PATTERNS;
let _debounceMs = 30;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Normalises any URL to its pathname for reliable pattern matching.
 * Absolute URLs are parsed; relative URLs are used as-is.
 */
function getPathname(url) {
  if (!url) return '';
  try { return new URL(url, location.origin).pathname; } catch { return String(url); }
}

/**
 * Returns true when a GET request's pathname matches at least one of the
 * configured batchable patterns.
 *
 * @param {string} url
 * @param {RequestInit|undefined} init
 * @returns {boolean}
 */
function isBatchable(url, init) {
  if ((init?.method ?? 'GET').toUpperCase() !== 'GET') return false;
  const pathname = getPathname(url);
  return _patterns.some(p =>
    p instanceof RegExp ? p.test(pathname) : pathname.startsWith(p)
  );
}

/**
 * Converts the flat headers object from the subrequests bundle into a plain
 * object keyed by lowercase header name, taking the first value from the
 * array that Drupal provides.
 *
 * @param {Record<string, string[]>} rawHeaders
 * @returns {Record<string, string>}
 */
function buildHeadersMap(rawHeaders) {
  const map = {};
  for (const [k, v] of Object.entries(rawHeaders ?? {})) {
    if (k !== 'status') {
      map[k.toLowerCase()] = Array.isArray(v) ? v[0] : v;
    }
  }
  return map;
}

/**
 * Formats a headers map into the CRLF-separated string expected by the
 * XMLHttpRequest getAllResponseHeaders() spec.
 *
 * @param {Record<string, string>} headersMap
 * @returns {string}
 */
function formatAllResponseHeaders(headersMap) {
  return Object.entries(headersMap)
    .map(([k, v]) => `${k}: ${v}`)
    .join('\r\n');
}

// ---------------------------------------------------------------------------
// fetch — synthetic Response factory
//
// The body of each sub-response is always a raw string. The batcher never
// parses it: callers receive a synthetic Response with the correct status and
// content-type and can call .json(), .text(), etc. themselves.
// A 404 body (e.g. an HTML Drupal error page) will produce response.ok===false
// exactly as a real fetch would.
// ---------------------------------------------------------------------------

/**
 * Builds a synthetic fetch Response from a subrequests bundle entry.
 *
 * @param {{ headers: Record<string, string[]>, body: string }} part
 * @returns {Response}
 */
function makeResponse(part) {
  const status = parseInt(part.headers?.status?.[0] ?? '200', 10);
  const headersMap = buildHeadersMap(part.headers);
  return new Response(part.body, { status, headers: headersMap });
}

// ---------------------------------------------------------------------------
// XHR synthetic response
//
// XMLHttpRequest is stateful: the lifecycle is open() → send() → events.
// Unlike fetch, XHR does not return a Promise; it communicates results by
// mutating instance properties and firing DOM events.
//
// The challenge is that core XHR properties — readyState, status, statusText,
// responseText, response — are defined as read-only getters on the native
// XMLHttpRequest prototype. Direct assignment (xhr.status = 200) is silently
// ignored even in strict mode. The only way to override them is to shadow
// each property on the specific XHR instance using Object.defineProperty with
// a custom getter. The configurable: true flag is set so the property can be
// re-defined if needed (e.g. during tests).
//
// Header accessors (getResponseHeader, getAllResponseHeaders) are replaced as
// plain functions on the instance — not on the prototype — so only this
// specific XHR object is affected.
//
// Event order mandated by the XHR spec (and relied upon by axios and other
// libraries):
//   readystatechange (readyState=4) → load → loadend
// ---------------------------------------------------------------------------

/**
 * Simulates a successful XHR response by overriding the instance's read-only
 * properties and dispatching events in the correct spec order.
 *
 * @param {XMLHttpRequest} xhr  - the XHR instance that was intercepted
 * @param {{ headers: Record<string, string[]>, body: string }} part
 */
export function resolveXhr(xhr, part) {
  const status = parseInt(part.headers?.status?.[0] ?? '200', 10);
  const headersMap = buildHeadersMap(part.headers);

  // Override read-only XHR properties on this instance only.
  // Direct assignment (xhr.status = 200) is silently ignored because these
  // properties are defined as non-writable getters on the XHR prototype.
  // configurable: true allows the property to be re-defined later if needed.
  Object.defineProperties(xhr, {
    readyState:   { get: () => 4,                  configurable: true },
    status:       { get: () => status,             configurable: true },
    statusText:   { get: () => 'OK',               configurable: true },
    responseText: { get: () => part.body,          configurable: true },
    response:     { get: () => part.body,          configurable: true },
    responseURL:  { get: () => xhr._batchUrl ?? '', configurable: true },
  });

  // Override header accessors on this instance (not the prototype) so only
  // this XHR is affected. Headers from the subrequests bundle are arrays of
  // strings; we take the first value, matching standard HTTP semantics.
  xhr.getResponseHeader = (name) => headersMap[name.toLowerCase()] ?? null;
  xhr.getAllResponseHeaders = () => formatAllResponseHeaders(headersMap);

  // Fire events in spec order: readystatechange signals readyState=4 (DONE),
  // then load signals success, then loadend signals completion regardless of
  // outcome. axios listens to all three; omitting any can cause silent hangs.
  xhr.dispatchEvent(new Event('readystatechange'));
  xhr.dispatchEvent(new ProgressEvent('load'));
  xhr.dispatchEvent(new ProgressEvent('loadend'));
}

// ---------------------------------------------------------------------------
// XHR error path
//
// When the subrequests POST itself fails (network error, Drupal 500 on the
// batch endpoint), we simulate a network error for every pending XHR caller.
// Per the XHR spec, a network error means: readyState=4, status=0,
// responseText='', and the 'error' event fires before 'loadend'.
// This is exactly what axios receives for a real network failure, ensuring it
// triggers its own error handling (Promise rejection, interceptors, etc.).
// ---------------------------------------------------------------------------

/**
 * Simulates a network-level XHR error (status 0, no response body).
 *
 * @param {XMLHttpRequest} xhr
 */
function rejectXhr(xhr) {
  // readyState must be 4 (DONE) even for error paths — the spec requires it.
  Object.defineProperty(xhr, 'readyState', { get: () => 4, configurable: true });
  xhr.dispatchEvent(new ProgressEvent('error'));
  xhr.dispatchEvent(new ProgressEvent('loadend'));
}

// ---------------------------------------------------------------------------
// Shared flush logic
//
// Both fetch and XHR entries share the same _pending[] list and the same
// debounce timer. When the timer fires, all pending entries are extracted,
// a single subrequests blueprint is built, and the POST is sent.
//
// Single-request optimisation: if only one request arrived during the
// debounce window, we send it directly (bypassing subrequests) to avoid
// the overhead of the batch endpoint for lone requests.
// ---------------------------------------------------------------------------

/**
 * Sends a single fetch request directly, bypassing subrequests.
 * Used when only one request lands in the batch window.
 */
function passThroughFetch(entry) {
  _nativeFetch(entry.url, entry.fetchInit ?? { credentials: 'include' })
    .then(entry.resolve)
    .catch(entry.reject);
}

/**
 * Sends a single XHR request directly, bypassing subrequests.
 * Used when only one XHR lands in the batch window.
 */
function passThroughXhr(entry) {
  const { xhrEntry: xhr, xhrBody } = entry;
  // Restore the prototype send so this XHR behaves normally.
  // We call _batchSend on the xhr after clearing the _batchable flag so the
  // xhrPatch.js CSRF branch can still add credentials/token if needed.
  xhr._batchable = false;
  XMLHttpRequest.prototype._batchSend.call(xhr, xhrBody);
}

async function flushBatch() {
  const batch = _pending.splice(0);
  _timer = null;

  if (batch.length === 0) return;

  // Single-request optimisation: avoid subrequests round-trip overhead.
  if (batch.length === 1) {
    const entry = batch[0];
    if (entry.xhrEntry) {
      passThroughXhr(entry);
    } else {
      passThroughFetch(entry);
    }
    return;
  }

  // Build the subrequests blueprint. URIs are kept as provided (relative or
  // absolute) — Drupal's subrequests module handles both.
  const blueprint = batch.map(entry => ({
    requestId: entry.requestId,
    uri: entry.url,
    action: 'view',
    headers: { Accept: 'application/json' },
  }));

  let bundle;
  try {
    const token = await ensureCsrfToken();
    const headers = {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
    };
    if (token) headers['X-CSRF-Token'] = token;

    const res = await _nativeFetch('/subrequests?_format=json', {
      method: 'POST',
      credentials: 'include',
      headers,
      body: JSON.stringify(blueprint),
    });

    if (!res.ok) {
      throw new Error(`Subrequests failed with HTTP ${res.status}`);
    }

    bundle = await res.json();
  } catch {
    // The subrequests POST itself failed (e.g. a 403 for a user lacking the
    // 'issue subrequests' permission, or a transient outage). Fail OPEN, not
    // closed: degrade to individual direct requests so one batch-endpoint
    // failure does not blank every batched page. These GETs are same-origin,
    // credentialed, and access-checked server-side, so each caller still gets
    // its own correct (access-checked) result or its own error.
    for (const entry of batch) {
      if (entry.xhrEntry) {
        passThroughXhr(entry);
      } else {
        passThroughFetch(entry);
      }
    }
    return;
  }

  // Resolve each original caller with its synthetic response.
  // Track URLs we have already resolved so that duplicate requests within the
  // same batch window get a clone of the first response rather than a second
  // read of the (already-consumed) body stream.
  const resolvedResponses = new Map(); // url → Response

  for (const entry of batch) {
    const part = bundle[entry.requestId];
    if (!part) {
      const err = new Error(`No subrequest response for ${entry.requestId} (${entry.url})`);
      if (entry.xhrEntry) rejectXhr(entry.xhrEntry);
      else entry.reject(err);
      continue;
    }

    if (entry.xhrEntry) {
      resolveXhr(entry.xhrEntry, part);
    } else {
      // Deduplicate: if a prior entry already built a Response for this URL,
      // clone it (a Response body can only be consumed once).
      if (resolvedResponses.has(entry.url)) {
        entry.resolve(resolvedResponses.get(entry.url).clone());
      } else {
        const response = makeResponse(part);
        resolvedResponses.set(entry.url, response);
        entry.resolve(response.clone()); // keep original for possible future clones
      }
    }
  }
}

/**
 * Registers an entry in the pending batch and (re)starts the debounce timer.
 */
function scheduleFlush() {
  clearTimeout(_timer);
  _timer = setTimeout(flushBatch, _debounceMs);
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Wraps window.fetch to intercept batchable GET requests.
 * Must be called explicitly from main.jsx, after fetchPatch.js has already
 * wrapped window.fetch, so the CSRF layer is preserved beneath the batcher.
 * Disable by setting VITE_ORBIT_REQUEST_BATCHER=false in .env.local.
 */
export function installRequestBatcher() {
  if (import.meta.env?.VITE_ORBIT_REQUEST_BATCHER === 'false') return;

  const _previousFetch = window.fetch.bind(window);

  window.fetch = function batchedFetch(input, init) {
    const rawUrl = typeof input === 'string'
      ? input
      : (input instanceof Request ? input.url : null);

    if (!rawUrl || !isBatchable(rawUrl, init)) {
      return _previousFetch(input, init);
    }

    const requestId = `rjb-${++_counter}`;
    return new Promise((resolve, reject) => {
      _pending.push({ requestId, url: rawUrl, fetchInit: init, resolve, reject, xhrEntry: null });
      scheduleFlush();
    });
  };
}

/**
 * Called from xhrPatch.js when a batchable XHR fires send().
 * The XHR object is held (not sent) until flushBatch() resolves it.
 *
 * @param {XMLHttpRequest} xhr       - the intercepted XHR instance
 * @param {string}         url       - URL captured at open() time
 * @param {Document|XMLHttpRequestBodyInit|null} body - body passed to send()
 */
export function enqueueXhr(xhr, url, body) {
  const requestId = `rjb-${++_counter}`;
  xhr._batchUrl = url; // stored for responseURL override in resolveXhr
  _pending.push({ requestId, url, xhrEntry: xhr, xhrBody: body, resolve: null, reject: null });
  scheduleFlush();
}

/**
 * Returns true when the given URL+method combination would be intercepted by
 * the batcher. Used by xhrPatch.js to set _batchable at open() time.
 *
 * @param {string} url
 * @param {string} method
 * @returns {boolean}
 */
export function isBatchableRequest(url, method) {
  if (import.meta.env?.VITE_ORBIT_REQUEST_BATCHER === 'false') return false;
  return isBatchable(url, { method });
}
